// Port of SuiteModel/ReadingMarks.swift: passages marked in a scene's text for reading (the Revise room's underlines) —
// each finding's passage found again in the current text, and the text split into paragraphs with the marks inside each
// as UTF-16 ranges, ready for the reader to draw. Exact.
import { nearest } from '../quoteSearch.ts';
import { trimCFWhitespace } from '../unicode.ts';
import { utf16OffsetOfScalar } from '../automerge/offsets.ts';
import { fromScalars, toScalars } from '../text/scalars.ts';

export type ReadingParagraphKind = 'text' | 'sceneBreak' | 'quotation';

export interface ReadingMark {
  id: string;
  /** UTF-16 [from, to) within the paragraph's text. */
  range: [number, number];
}

export interface ReadingParagraph {
  kind: ReadingParagraphKind;
  /** The paragraph's Markdown, without its trailing newline. */
  text: string;
  /** Where it starts in the scene (Unicode scalars). */
  start: number;
  marks: ReadingMark[];
}

/** `ReadingMarks.resolve`: where a passage is now — at `range` if the text there is still `quote`, else the same words
 *  nearest where they were, else null (it's been rewritten). Scalars. */
export function resolvePassage(quote: string, range: [number, number], text: string): [number, number] | null {
  const scalars = toScalars(text);
  if (range[0] >= 0 && range[1] <= scalars.length && range[0] <= range[1] && fromScalars(scalars, range[0], range[1]) === quote) return range;
  if (quote === '') return null;
  return nearest(quote, Math.max(0, range[0]), text);
}

/** `ReadingMarks.paragraphs`: the text's paragraphs (blank lines dropped) with the marks that fall in each, clipped. */
export function readingParagraphs(text: string, marks: readonly { id: string; range: [number, number] }[]): ReadingParagraph[] {
  const scalars = toScalars(text);
  const out: ReadingParagraph[] = [];
  let start = 0;
  const add = (end: number) => {
    const body = fromScalars(scalars, start, end);
    const trimmed = trimCFWhitespace(body);
    if (trimmed === '') return;
    const kind: ReadingParagraphKind = trimmed === '***' || trimmed === '* * *' ? 'sceneBreak' : trimmed.startsWith('>') ? 'quotation' : 'text';
    const inside: ReadingMark[] = [];
    for (const m of marks) {
      const lower = Math.max(m.range[0], start), upper = Math.min(m.range[1], end);
      if (lower >= upper) continue;
      inside.push({ id: m.id, range: [utf16OffsetOfScalar(body, lower - start), utf16OffsetOfScalar(body, upper - start)] });
    }
    out.push({ kind, text: body, start, marks: inside });
  };
  scalars.forEach((c, i) => {
    if (c === '\n') {
      add(i);
      start = i + 1;
    }
  });
  if (start < scalars.length) add(scalars.length);
  return out;
}

export const ReadingMarks = { resolve: resolvePassage, paragraphs: readingParagraphs };
